import type { Broadcast, BroadcastPreset } from './types.ts';
import { BROADCAST_PRESET_TEXT, spokenAircraftName, spokenName } from './callout.ts';

/**
 * Crew comms on a shoot (docs/CREW_COMMS.md). Once the aircraft is up, shooters listen (AirPods)
 * rather than look, so every broadcast is a short spoken line. Two kinds of call:
 *
 *   flight      what the aircraft is doing — the producer / pilot liaison relays it from the
 *               radio or the ramp. Public ADS-B lags or is missing for many flight-test aircraft,
 *               so the human call is the reliable "it's moving" signal.
 *   production  what the cameras should do (hold, go, reset, next pass, reposition, wrap).
 *
 * Everything else is plain team chat. Pure; shared by the server (spoken lines) and the web (send pad).
 */

export type BroadcastGroup = 'flight' | 'production' | 'custom';

export interface BroadcastCall {
  preset: BroadcastPreset;
  group: BroadcastGroup;
  /** Button label. */
  label: string;
  /** Second line on the button (what it means), optional. */
  hint?: string;
  /** Stored / shown text (BROADCAST_PRESET_TEXT: "TAXIING", "NEXT PASS"). */
  text: string;
  /** What the voice says, without the aircraft name ("taxiing"). */
  say: string;
}

export const BROADCAST_CALLS: readonly BroadcastCall[] = Object.freeze(([
  { preset: 'ENGINES_START', group: 'flight', label: 'Engines start', say: 'engines starting' },
  { preset: 'TAXIING', group: 'flight', label: 'Taxiing', say: 'taxiing' },
  { preset: 'TAKEOFF', group: 'flight', label: 'Wheels up', hint: 'takeoff', say: 'takeoff, wheels up' },
  { preset: 'ON_STATION', group: 'flight', label: 'On station', hint: 'in the area', say: 'on station' },
  { preset: 'STARTING_PASS', group: 'flight', label: 'Starting pass', say: 'starting the pass' },
  { preset: 'PASS_COMPLETE', group: 'flight', label: 'Pass complete', say: 'pass complete' },
  { preset: 'RTB', group: 'flight', label: 'Returning', hint: 'RTB', say: 'returning to base' },
  { preset: 'LANDING', group: 'flight', label: 'Landing', say: 'landing' },
  { preset: 'SHUTDOWN', group: 'flight', label: 'Shutdown', hint: 'wheels down', say: 'wheels down, shutting down' },
  { preset: 'HOLD', group: 'production', label: 'Hold', say: 'hold, hold' },
  { preset: 'GO', group: 'production', label: 'Go', hint: 'rolling', say: 'go, rolling' },
  { preset: 'RESET', group: 'production', label: 'Reset', say: 'reset' },
  { preset: 'NEXT_PASS', group: 'production', label: 'Next pass', say: 'next pass, stand by' },
  { preset: 'REPOSITION', group: 'production', label: 'Reposition', say: 'reposition' },
  { preset: 'WRAP', group: 'production', label: 'Wrap', say: "that's a wrap" },
] satisfies Omit<BroadcastCall, 'text'>[]).map((c) => ({ ...c, text: BROADCAST_PRESET_TEXT[c.preset] })));

const BY_PRESET = new Map<BroadcastPreset, BroadcastCall>(BROADCAST_CALLS.map((c) => [c.preset, c]));

/** The call for a preset (null for CUSTOM or an unknown stored value). */
export function broadcastCall(preset: BroadcastPreset | string): BroadcastCall | null {
  return BY_PRESET.get(preset as BroadcastPreset) ?? null;
}

/** Every preset value the server accepts (old values included). */
export const BROADCAST_PRESETS: readonly BroadcastPreset[] = Object.freeze([...BROADCAST_CALLS.map((c) => c.preset), 'CUSTOM' as const]);

export const isFlightCall = (preset: BroadcastPreset | string): boolean => broadcastCall(preset)?.group === 'flight';

/**
 * The likely next call after `preset` (first = highlighted). Flight calls follow the flight; a
 * production call suggests what normally follows it on a pass.
 */
const NEXT: Partial<Record<BroadcastPreset, BroadcastPreset[]>> = {
  ENGINES_START: ['TAXIING'],
  TAXIING: ['TAKEOFF'],
  TAKEOFF: ['ON_STATION'],
  ON_STATION: ['STARTING_PASS'],
  STARTING_PASS: ['PASS_COMPLETE', 'HOLD'],
  PASS_COMPLETE: ['NEXT_PASS', 'RTB'],
  NEXT_PASS: ['STARTING_PASS'],
  RESET: ['STARTING_PASS'],
  REPOSITION: ['STARTING_PASS'],
  HOLD: ['GO', 'RESET'],
  GO: ['PASS_COMPLETE'],
  RTB: ['LANDING'],
  LANDING: ['SHUTDOWN'],
  SHUTDOWN: ['WRAP', 'ENGINES_START'],
};

/** Suggested next calls given the shoot's broadcasts so far (custom text is skipped). Empty after WRAP. */
export function nextCalls(broadcasts: readonly Pick<Broadcast, 'preset' | 't'>[]): BroadcastPreset[] {
  let last: BroadcastPreset | null = null;
  let lastT = -Infinity;
  for (const b of broadcasts) if (b.preset !== 'CUSTOM' && broadcastCall(b.preset) && b.t >= lastT) (last = b.preset), (lastT = b.t);
  if (!last) return ['ENGINES_START'];
  return NEXT[last] ?? [];
}

/** The latest flight-phase call ("where the aircraft is"), or null. */
export function lastFlightCall<T extends Pick<Broadcast, 'preset' | 't'>>(broadcasts: readonly T[]): T | null {
  let out: T | null = null;
  for (const b of broadcasts) if (isFlightCall(b.preset) && (!out || b.t >= out.t)) out = b;
  return out;
}

/** Stored text for a preset broadcast ("TAXIING"); CUSTOM keeps the sender's text. */
export function broadcastText(preset: BroadcastPreset, custom?: string | null): string {
  if (preset === 'CUSTOM') return (custom ?? '').trim();
  return broadcastCall(preset)?.text ?? (custom ?? '').trim();
}

const sentence = (s: string): string => {
  const t = s.trim().replace(/[.!?]+$/, '');
  return t ? `${t[0]!.toUpperCase()}${t.slice(1)}.` : '';
};

/**
 * What every listener hears for a broadcast — short, no sender for preset calls (the sender shows
 * on screen), the aircraft named for flight calls when the shoot has one subject:
 *   TAXIING + "Heron"      → "Heron, taxiing."
 *   TAKEOFF                   → "Takeoff, wheels up."
 *   HOLD                      → "Hold, hold."
 *   CUSTOM "Swap to the 300"  → "Swap to the 300. From Ines."
 */
export function broadcastSpokenLine(preset: BroadcastPreset, text: string, fromName: string, aircraftLabel?: string | null): string {
  const call = broadcastCall(preset);
  if (!call) {
    const body = sentence(text);
    return body ? `${body} From ${spokenName(fromName, { keepSerial: true }) || 'the producer'}.` : '';
  }
  if (call.group === 'flight' && aircraftLabel) return sentence(`${spokenAircraftName(aircraftLabel)}, ${call.say}`);
  return sentence(call.say);
}

